// UI strings added in the reports audit (existing strings stay in utils/lang).

const TEXT = {
  en: {
    pendingApproval: "Pending approval",
    period: {
      label: "Reporting period",
      from: "From",
      to: "To",
      apply: "Apply",
      last12: "Last 12 months",
      updating: "Updating the report…",
      applied: (from, to, single) => (single ? `Showing ${from}` : `Showing ${from} to ${to}`),
      previousYear: "Previous year",
      nextYear: "Next year",
    },
    meta: {
      apps: (n) => `${n} ${n === 1 ? "app" : "apps"}`,
      branches: (n) => `${n} ${n === 1 ? "branch" : "branches"}`,
      records: (n) => `${n} ${n === 1 ? "record" : "records"}`,
    },
    deleteScope: ({ count, report, from, to, stores, more, filtered }) =>
      `This permanently deletes ${count} record(s) from the ${report} report. Period: ${from === to ? from : `${from} to ${to}`}. Stores: ${stores}${more ? ` and ${more} more` : ""}.${filtered ? " Only records matching the other active filters are included." : ""}`,
    deleteScopeChanged: "These records changed after the preview, so nothing was deleted. Review the report and try again.",
    approveMonth: "Approve month",
    disapproveMonth: "Disapprove month",
    approveApp: "Approve app for this date",
    disapproveApp: "Disapprove app for this date",
    edit: "Edit",
    delete: "Delete",
    export: "Export",
    exportLabel: (count) => `Export ${count} records to Excel`,
    deleteFiltered: "Delete all filtered records",
    deleteFilteredDescription: (count) =>
      `This permanently deletes ${count} record(s): every record matching the current filters.`,
    deleteAllWarning: "No filters are applied, so this deletes every record in this report.",
    actionFailed: "The change could not be saved. Please try again.",
    loadFailed: "The report could not be loaded.",
    retry: "Try again",
    loading: "Loading report…",
    empty: "No records match the current filters.",
    toggleRow: (label) => `Show or hide details for ${label}`,
    pagination: {
      nav: "Pages",
      previous: "Previous",
      next: "Next",
      summary: (page, pages, total) => `Page ${page}${pages ? ` of ${pages}` : ""}${total != null ? ` - ${total} records` : ""}`,
      limit: "Narrow the filters to see more.",
    },
    windowNote: (from, to, clamped) => `Showing ${from} to ${to}${clamped ? " (limited to the last 24 months)" : ""}`,
    loadEarlier: "Load earlier",
    analysisWindow: {
      label: "Analysis period",
      from: "From month",
      to: "To month",
      last12: "Last 12 months",
      last24: "Last 24 months",
      shown: (from, to) => `Showing ${from} to ${to}`,
      capped: "Analysis is limited to 24 months at a time.",
      loading: "Loading analysis…",
      empty: "No records in this period. Choose another period or load earlier months.",
      error: "The analysis could not be loaded. Please try again.",
      errors: {
        format: "Choose a valid month for both dates.",
        order: "The start month must not be after the end month.",
        future: "The end month cannot be in the future.",
        tooLong: "The period can cover at most 24 months.",
      },
    },
    tooManyRows: "Too many records match these filters. Narrow the filters and try again.",
    exportFailed: "The export could not be created. Please try again.",
    exporting: "Preparing export…",
    sortBy: "Sort by",
    sortOptions: { dateDesc: "Date: newest first", dateAsc: "Date: oldest first", amountDesc: "Amount: high to low", amountAsc: "Amount: low to high" },
    checkingRecords: "Checking the records to delete…",
    deleteListFailed: "The records to delete could not be listed. Nothing was deleted.",
    rowsLoading: "Loading rows…",
    rowsEmpty: "No records in this period.",
  },
  ar: {
    pendingApproval: "بانتظار الاعتماد",
    period: {
      label: "فترة التقرير",
      from: "من",
      to: "إلى",
      apply: "تطبيق",
      last12: "آخر 12 شهراً",
      updating: "جارٍ تحديث التقرير…",
      applied: (from, to, single) => (single ? `عرض ${from}` : `عرض من ${from} إلى ${to}`),
      previousYear: "السنة السابقة",
      nextYear: "السنة التالية",
    },
    meta: {
      apps: (n) => `${n} تطبيق`,
      branches: (n) => `${n} فرع`,
      records: (n) => `${n} سجل`,
    },
    deleteScope: ({ count, report, from, to, stores, more, filtered }) =>
      `سيتم حذف ${count} سجل نهائياً من تقرير ${report}. الفترة: ${from === to ? from : `من ${from} إلى ${to}`}. المتاجر: ${stores}${more ? ` و${more} أخرى` : ""}.${filtered ? " تشمل السجلات المطابقة لبقية الفلاتر المطبقة فقط." : ""}`,
    deleteScopeChanged: "تغيرت هذه السجلات بعد المعاينة، لذلك لم يُحذف شيء. راجع التقرير وحاول مرة أخرى.",
    approveMonth: "اعتماد الشهر",
    disapproveMonth: "إلغاء اعتماد الشهر",
    approveApp: "اعتماد التطبيق لهذا اليوم",
    disapproveApp: "إلغاء اعتماد التطبيق لهذا اليوم",
    edit: "تعديل",
    delete: "حذف",
    export: "تصدير",
    exportLabel: (count) => `تصدير ${count} سجل إلى Excel`,
    deleteFiltered: "حذف جميع السجلات المفلترة",
    deleteFilteredDescription: (count) =>
      `سيتم حذف ${count} سجل نهائياً: كل السجلات المطابقة للفلاتر الحالية.`,
    deleteAllWarning: "لا توجد فلاتر مطبقة، لذلك سيتم حذف جميع سجلات هذا التقرير.",
    actionFailed: "تعذر حفظ التغيير. حاول مرة أخرى.",
    loadFailed: "تعذر تحميل التقرير.",
    retry: "إعادة المحاولة",
    loading: "جارٍ تحميل التقرير…",
    empty: "لا توجد سجلات مطابقة للفلاتر الحالية.",
    toggleRow: (label) => `إظهار أو إخفاء تفاصيل ${label}`,
    pagination: {
      nav: "الصفحات",
      previous: "السابق",
      next: "التالي",
      summary: (page, pages, total) => `الصفحة ${page}${pages ? ` من ${pages}` : ""}${total != null ? ` - ${total} سجل` : ""}`,
      limit: "ضيّق الفلاتر لعرض المزيد.",
    },
    windowNote: (from, to, clamped) => `عرض من ${from} إلى ${to}${clamped ? " (محدود بآخر 24 شهراً)" : ""}`,
    loadEarlier: "تحميل الأقدم",
    analysisWindow: {
      label: "فترة التحليل",
      from: "من شهر",
      to: "إلى شهر",
      last12: "آخر 12 شهراً",
      last24: "آخر 24 شهراً",
      shown: (from, to) => `عرض من ${from} إلى ${to}`,
      capped: "التحليل محدود بـ 24 شهراً في المرة الواحدة.",
      loading: "جارٍ تحميل التحليل…",
      empty: "لا توجد سجلات في هذه الفترة. اختر فترة أخرى أو حمّل أشهراً أقدم.",
      error: "تعذر تحميل التحليل. حاول مرة أخرى.",
      errors: {
        format: "اختر شهراً صحيحاً للتاريخين.",
        order: "يجب ألا يكون شهر البداية بعد شهر النهاية.",
        future: "لا يمكن أن يكون شهر النهاية في المستقبل.",
        tooLong: "يمكن أن تغطي الفترة 24 شهراً كحد أقصى.",
      },
    },
    tooManyRows: "عدد السجلات المطابقة كبير جداً. ضيّق الفلاتر وحاول مرة أخرى.",
    exportFailed: "تعذر إنشاء ملف التصدير. حاول مرة أخرى.",
    exporting: "جارٍ تجهيز ملف التصدير…",
    sortBy: "الترتيب حسب",
    sortOptions: { dateDesc: "التاريخ: الأحدث أولاً", dateAsc: "التاريخ: الأقدم أولاً", amountDesc: "المبلغ: من الأعلى إلى الأدنى", amountAsc: "المبلغ: من الأدنى إلى الأعلى" },
    checkingRecords: "جارٍ التحقق من السجلات المراد حذفها…",
    deleteListFailed: "تعذر جلب قائمة السجلات المراد حذفها. لم يُحذف شيء.",
    rowsLoading: "جارٍ تحميل الصفوف…",
    rowsEmpty: "لا توجد سجلات في هذه الفترة.",
  },
};

export const getReportText = (lang) => TEXT[lang] || TEXT.en;

// Accessible name for an inline edit input (field keys are the record keys).
const EXTRA_AR_FIELDS = {
  orderStatus: "حالة الطلب",
  orderAmount: "قيمة الطلب",
  cause: "السبب",
  notes: "ملاحظات",
  paidCampains: "الحملات المدفوعة",
};

export const getFieldLabel = (lang, key, arLabels = {}) => {
  if (lang === "ar") return arLabels[key] || EXTRA_AR_FIELDS[key] || key;
  const words = String(key).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
